import type {
  ApiError,
  ApiResponse,
  PreparePaymentRequest,
  PreparePaymentResponse,
  PaymentStatusResponse,
  SaleResponse,
} from "@/types";
import type {
  CoopercoPaymentInitResponse,
  CoopercoPaymentStatusResponse,
  DeunaPaymentInitResponse,
  DeunaPaymentStatus,
  DeunaPaymentStatusResponse,
  IPaymentService,
} from "../types";
import { legacyHttpClient } from "@services/http/legacyHttpClient";
import { env } from "@services/config/env";
import { logger } from "@shared/utils";

/**
 * RF-5 (bug-20261001-venta-cerrada-se-lee-como-pagada) — el estado de la venta, leído del texto
 * y no deducido de un número.
 *
 * Antes esto era `sale.state === 0 ? "PAGADO" : "PENDIENTE"`, y ahí estaba el problema: el
 * backend devolvía `state: 0` para TODA venta que no fuera confirmada ni pendiente, porque esos
 * casos no entraban en ninguna rama de su mapeo y el `int` se quedaba en su valor por defecto.
 * Una venta expirada, rechazada o anulada se leía como PAGADA. El backend ya lo arregla; acá
 * dejamos de depender de ese número para lo que el texto dice sin ambigüedad.
 *
 * `estadoPago` es opcional: una venta anterior al cambio puede no traerlo, y ahí `state` sigue
 * siendo el respaldo — ahora sí confiable, pero respaldo.
 */
function estadoDeVenta(sale: SaleResponse): PaymentStatusResponse["status"] {
  switch (sale.estadoPago?.toUpperCase()) {
    case "APROBADO":
      return "PAGADO";
    case "EXPIRADO":
      return "EXPIRADO";
    case "RECHAZADO":
      return "RECHAZADO";
    case "ANULADO":
      return "ANULADO";
    case "PENDIENTE":
      return "PENDIENTE";
  }
  // Sin `estadoPago`: el código numérico. 0 = confirmada, 2 = cerrada sin cobro, 9 = anulada.
  // Cualquier otro valor es "todavía no se sabe", que es lo único honesto que se puede decir.
  if (sale.state === 0) return "PAGADO";
  if (sale.state === 2) return "RECHAZADO";
  if (sale.state === 9) return "ANULADO";
  return "PENDIENTE";
}

/**
 * Convert a currency amount to integer cents for PayPhone SDK.
 * Handles string (with commas) and number inputs, normalizes to cents.
 * @param value - Currency amount as string or number (e.g., "1,234.56" or 1234.56)
 * @returns Amount in cents as integer (e.g., 123456 for $1234.56)
 */
function toIntegerCents(value: string | number | undefined): number {
  if (typeof value === "number")
    return Number.isFinite(value) ? Math.round(value) : 0;
  if (!value) return 0;
  const normalized = value.replace(/,/g, "").trim();
  const parsed = Number.parseFloat(normalized);
  if (Number.isNaN(parsed)) return 0;
  return Math.round(parsed);
}

/**
 * Extract PayPhone redirect URL from various possible response field names.
 * PayPhone API may return deeplink, deepLink, url, checkoutUrl, or paymentUrl.
 * @param data - Backend response object containing payment redirect info
 * @returns Redirect URL string, or empty string if not found
 */
function extractRedirectUrl(data: Record<string, unknown>): string {
  const candidates = [
    data.deeplink,
    data.deepLink,
    data.url,
    data.checkoutUrl,
    data.paymentUrl,
  ];

  const found = candidates.find(
    (candidate): candidate is string =>
      typeof candidate === "string" && candidate.trim().length > 0,
  );

  return found ?? "";
}

const DEUNA_STATUS_VALUES: DeunaPaymentStatus[] = [
  "PENDING",
  "APPROVED",
  "EXPIRED",
  "REVERSED",
  "FAILED",
];

function parseDeunaStatus(value: unknown): DeunaPaymentStatus | undefined {
  if (typeof value !== "string") return undefined;
  const normalized = value.toUpperCase();
  return DEUNA_STATUS_VALUES.includes(normalized as DeunaPaymentStatus)
    ? (normalized as DeunaPaymentStatus)
    : undefined;
}

/**
 * Production payment service — uses the legacy backend for sale data
 * and redirects to PayPhone for card payments.
 */
export const realPaymentService: IPaymentService = {
  async preparePayphone(
    data: PreparePaymentRequest,
  ): Promise<ApiResponse<PreparePaymentResponse>> {
    try {
      // The legacy backend creates a sale, then we redirect to PayPhone
      // get_sale_by_id returns the sale with amounts in cents for PayPhone
      const sale = await legacyHttpClient.post<SaleResponse>(
        "/api/web/get_sale_by_id",
        { id: data.orderId },
      );

      // Extract data for PayPhone SDK (embedded form, not redirect)
      logger.debug("[PayPhone preparePayphone] Backend sale response:", sale);
      const storeId = sale.storeId ?? env.payPhoneStoreId;
      const amountCents = toIntegerCents(sale.totalpb);
      const amountWithoutTaxCents = amountCents;
      const reference = sale.reference ?? sale.id;
      const clientTxId = sale.clientTransactionId ?? sale.id;
      logger.debug(
        "[PayPhone preparePayphone] clientTransactionId:",
        clientTxId,
      );

      // Get token from environment variable (PayPhone SDK requires tokenAuth)
      const tokenAuth = env.payPhoneTokenAuth;

      return {
        success: true,
        data: {
          orderId: data.orderId,
          total: Number.parseFloat(String(sale.total)) || 0,
          tokenAuth,
          clientTransactionId: clientTxId,
          amount: amountCents,
          amountWithoutTax: amountWithoutTaxCents,
          storeId,
          reference: `Pago orden ${reference}`,
        },
      };
    } catch (error) {
      return {
        success: false,
        data: { orderId: "", total: 0 },
        message:
          error instanceof Error ? error.message : "Error al preparar el pago",
      };
    }
  },

  async getStatus(
    orderId: string,
  ): Promise<ApiResponse<PaymentStatusResponse>> {
    try {
      const sale = await legacyHttpClient.post<SaleResponse>(
        "/api/web/get_sale_by_id",
        { id: orderId },
      );
      return {
        success: true,
        data: {
          orderId,
          status: estadoDeVenta(sale),
          total: Number(sale.total) || 0,
        },
      };
    } catch (error) {
      return {
        success: false,
        data: { orderId, status: "PENDIENTE", total: 0 },
        message:
          error instanceof Error ? error.message : "Error al consultar estado",
      };
    }
  },

  async handleReturn(): Promise<ApiResponse<void>> {
    // PayPhone return is handled by the URL redirect — no backend call needed
    return { success: true, data: undefined };
  },

  async handleCancel(): Promise<ApiResponse<void>> {
    // PayPhone cancel is handled by the URL redirect
    return { success: true, data: undefined };
  },

  async confirmManual(): Promise<ApiResponse<{ message: string }>> {
    // Manual confirmation (JEP/DeUna) is handled by the admin backend
    return {
      success: false,
      data: { message: "Confirmación manual no disponible en la web pública" },
    };
  },

  /**
   * Verify a PayPhone payment with the backend.
   * Mirrors web_ticketin's /consultar_payphone flow:
   * PayPhone redirects to /payment/return?id={orderId}&clientTransactionId={txId}
   * → we POST both to the legacy backend which queries PayPhone's API server-side.
   * Backend returns { status: 200 } on success, non-200 on failure.
   */
  async consultarPayphone(
    orderId: string,
    clientTransactionId: string,
  ): Promise<ApiResponse<void>> {
    try {
      // legacyHttpClient desenvuelve el sobre {status,message,data} y LANZA (ApiError)
      // si status != 200. Por lo tanto, si esta llamada NO lanza, el backend respondió
      // status 200 → PayPhone confirmó el pago como APROBADO (y generó los tickets).
      await legacyHttpClient.post(
        "/api/web/consultar_payphone",
        { id: orderId, transactionId: clientTransactionId },
      );
      return { success: true, data: undefined };
    } catch (error) {
      // El backend responde status 500 + "La transaccion no fue aprobada" cuando
      // PayPhone confirmó que el pago NO fue aprobado. Eso es un rechazo DEFINITIVO
      // (no un fallo transitorio de red), así que lo marcamos para que la UI muestre
      // "Pago rechazado" sin reintentar ni caer en el respaldo de get_sale_by_id.
      const apiError = error as ApiError | undefined;
      const status = apiError?.status ?? 0;
      const message = apiError?.message ?? "";
      const rejected = status === 500 && /no\s+(fue\s+)?aprobada/i.test(message);
      return {
        success: false,
        rejected,
        data: undefined,
        message: message || "Error al verificar el pago",
      };
    }
  },

  async confirmSale(orderId: string): Promise<ApiResponse<void>> {
    try {
      await legacyHttpClient.post<{ status: number; message?: string }>(
        "/api/web/confirm_sale",
        { id: orderId },
      );
      return { success: true, data: undefined };
    } catch (error) {
      return {
        success: false,
        data: undefined,
        message: error instanceof Error ? error.message : "Error al confirmar venta",
      };
    }
  },

  /**
   * Confirm a Plux payment. Backend re-verifies against Plux (PluxService.verificar)
   * and only if approved calls confirm_sale internally (QR + correo). Idempotente:
   * si la orden ya tiene códigos generados, el backend responde 200 sin reprocesar.
   */
  async confirmPlux(
    orden: string,
    idTransaccion: string,
  ): Promise<ApiResponse<void>> {
    try {
      await legacyHttpClient.post<string>("/api/web/plux/confirm", {
        orden,
        idTransaccion,
      });
      return { success: true, data: undefined };
    } catch (error) {
      return {
        success: false,
        data: undefined,
        message:
          error instanceof Error ? error.message : "Error al confirmar el pago con Plux",
      };
    }
  },

  async prepareDeUna(
    orderId: string,
    orderIds?: string[],
  ): Promise<ApiResponse<DeunaPaymentInitResponse>> {
    try {
      // Call backend endpoint to initiate DeUna payment and get QR
      const response = await legacyHttpClient.post<{
        qr: string;
        transactionId: string;
        numericCode?: string;
        ttlSeconds?: number;
        expiresAt?: string;
        internalTransactionReference?: string;
        status?: string;
        amount?: number;
        currency?: string;
        orderId?: string;
        orderIds?: string[];
        deeplink?: string;
        deepLink?: string;
        url?: string;
        checkoutUrl?: string;
        paymentUrl?: string;
      }>("/api/web/solicitud_pago", {
        orden: orderId,
        ordenes: orderIds && orderIds.length > 0 ? orderIds : undefined,
      });

      const redirectUrl = extractRedirectUrl(
        response as unknown as Record<string, unknown>,
      );

      const resolvedExpiresAt = response.expiresAt
        ? response.expiresAt
        : response.ttlSeconds
          ? new Date(Date.now() + response.ttlSeconds * 1000).toISOString()
          : undefined;

      const normalizedStatus = parseDeunaStatus(response.status);

      return {
        success: true,
        data: {
          qr: response.qr || "",
          orderId: response.orderId ?? orderId,
          orderIds:
            response.orderIds && response.orderIds.length > 0
              ? response.orderIds
              : orderIds && orderIds.length > 0
                ? orderIds
                : undefined,
          redirectUrl: redirectUrl || undefined,
          numericCode: response.numericCode,
          ttlSeconds: response.ttlSeconds,
          expiresAt: resolvedExpiresAt,
          transactionId: response.transactionId,
          internalTransactionReference: response.internalTransactionReference,
          status: normalizedStatus,
          amount: response.amount,
          currency: response.currency,
        },
      };
    } catch (error) {
      return {
        success: false,
        data: { qr: "", orderId },
        message:
          error instanceof Error
            ? error.message
            : "Error al iniciar pago DeUna",
      };
    }
  },

  async consultarDeuna(
    orderId: string,
  ): Promise<ApiResponse<DeunaPaymentStatusResponse>> {
    try {
      const response = await legacyHttpClient.post<DeunaPaymentStatusResponse>(
        "/api/web/consultar_deuna",
        { orderId },
      );

      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        data: { orderId, status: "PENDING" },
        message:
          error instanceof Error
            ? error.message
            : "Error al consultar estado DeUna",
      };
    }
  },

  async prepareCooperco(
    orderId: string,
  ): Promise<ApiResponse<CoopercoPaymentInitResponse>> {
    try {
      const response = await legacyHttpClient.post<{
        qr: string;
        codigo: string;
        expiresAt?: string;
        pollUntil?: string;
      }>("/api/web/cooperco/getqr", { id: orderId });

      return {
        success: true,
        data: {
          qr: response.qr ? `data:image/png;base64,${response.qr}` : "",
          codigo: response.codigo ?? "",
          orderId,
          expiresAt: response.expiresAt,
          pollUntil: response.pollUntil,
        },
      };
    } catch (error) {
      return {
        success: false,
        data: { qr: "", codigo: "", orderId },
        message:
          error instanceof Error
            ? error.message
            : "Error al iniciar pago Cooperco",
      };
    }
  },

  async consultarCooperco(
    orderId: string,
  ): Promise<ApiResponse<CoopercoPaymentStatusResponse>> {
    try {
      const response = await legacyHttpClient.post<{ status: string }>(
        "/api/web/cooperco/consultar",
        { id: orderId },
      );
      return {
        success: true,
        data: {
          orderId,
          status: response.status === "PAGADO" ? "PAGADO" : "INGRESADO",
        },
      };
    } catch (error) {
      return {
        success: false,
        data: { orderId, status: "INGRESADO" },
        message:
          error instanceof Error
            ? error.message
            : "Error al consultar estado Cooperco",
      };
    }
  },

  async prepareJEP(
    orderId: string,
  ): Promise<
    ApiResponse<{ qr: string; orderId: string; redirectUrl?: string }>
  > {
    try {
      // Call backend endpoint to initiate JEP payment and get QR
      const response = await legacyHttpClient.post<{
        qr: string;
        codigoTransaccion: string;
        deeplink?: string;
        deepLink?: string;
        url?: string;
        checkoutUrl?: string;
        paymentUrl?: string;
      }>("/api/web/solicitud_pago_jep", { orden: orderId });

      const redirectUrl = extractRedirectUrl(
        response as unknown as Record<string, unknown>,
      );

      return {
        success: true,
        data: {
          qr: response.qr ? `data:image/png;base64,${response.qr}` : "",
          orderId,
          redirectUrl: redirectUrl || undefined,
        },
      };
    } catch (error) {
      return {
        success: false,
        data: { qr: "", orderId },
        message:
          error instanceof Error ? error.message : "Error al iniciar pago JEP",
      };
    }
  },
};
